import { type ReactNode, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */

// Gradient style constants read from CSS custom properties so they track the
// theme (light-mode warm forest green vs dark-mode bright Tailwind green).
// Do not hardcode hex values here — see
// docs/help-sheet-homogenization-principles.md §2.

export const DASHED_REST_LINE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(to right, var(--scenario-green) 0, var(--scenario-green) 4px, transparent 4px, transparent 8px)',
  backgroundColor: 'transparent',
};

export const DASHED_REST_SWATCH: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(to right, var(--scenario-green) 0, var(--scenario-green) 3px, transparent 3px, transparent 6px)',
};

export const DASHED_SHORT_LINE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(to right, var(--destructive) 0, var(--destructive) 4px, transparent 4px, transparent 8px)',
  backgroundColor: 'transparent',
};

/* ------------------------------------------------------------------ */
/*  Shared text classes                                                */
/* ------------------------------------------------------------------ */

const HOURS_CLASS =
  'font-mono text-[10px] sm:text-[12px] leading-none font-semibold tabular-nums whitespace-nowrap shrink-0';

const STATUS_CLASS =
  'font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-wider shrink-0';

/* ------------------------------------------------------------------ */
/*  RestGapConnector — horizontal rest gap between shifts              */
/* ------------------------------------------------------------------ */

interface RestGapConnectorProps {
  hours: number;
  triggered: boolean;
  subLabel?: ReactNode;
  annotation?: ReactNode;
  labelText?: string;
  className?: string;
}

export function RestGapConnector({
  hours,
  triggered,
  subLabel,
  annotation,
  labelText,
  className,
}: RestGapConnectorProps) {
  const resolvedLabel = labelText ?? `Adequate Rest`;
  const isShortedLabel = triggered && resolvedLabel.toLowerCase().includes('short');
  const defaultSpacingClass = triggered ? 'pt-2 mb-2' : 'pt-2 mb-2';
  const lineStrokeClass = cn(
    'pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-px',
    isShortedLabel ? 'bg-destructive/80' : (triggered ? 'bg-primary-text' : 'bg-scenario-green/60'),
  );
  const lineTrackClass = 'relative flex-1 h-2 -my-1';
  const lineStyle = isShortedLabel ? DASHED_SHORT_LINE : (triggered ? undefined : DASHED_REST_LINE);

  return (
    <div className={cn('w-full px-1', className || defaultSpacingClass)}>
      <div className="flex items-center gap-2 w-full">
        <div className={lineTrackClass} data-measure-label={`${resolvedLabel} Divider (Left)`}>
          <div className={lineStrokeClass} style={lineStyle} />
        </div>
        <span className={cn(
          HOURS_CLASS,
          isShortedLabel ? 'text-destructive-text' : (triggered ? 'text-primary-text' : 'text-scenario-green'),
          subLabel && 'whitespace-normal text-center leading-tight',
        )}>
          {resolvedLabel}
          {subLabel && (
            <>
              <br />
              {subLabel}
            </>
          )}
        </span>
        <div className={lineTrackClass} data-measure-label={`${resolvedLabel} Divider (Right)`}>
          <div className={lineStrokeClass} style={lineStyle} />
        </div>
      </div>
      {annotation}
    </div>
  );
}
